import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { memo, useContext } from 'react';
import InlineMessage from '@ingka/inline-message';
import { t } from 'i18next';
import styles from '../../LotteryForm.module.scss';
import { ControlledInputField } from '../../components/controlled-input-field/ControlledInputField';
import { useFormModule } from '../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer, PropsAreStrings } from '../types';
import { LotteryFormContext } from '../../lottery-form-context';

export const MODULE_NAME = 'winners';

export type WinnersFormType = PropsAreStrings<Static<typeof WinnersFormSchema>>;
export const WinnersFormSchema = Type.Object(
  {
    nbrWinners: Type.Integer({
      minimum: 1,
      maximum: 20000,
      errorMessage: {
        type: 'form-error-message-numeric-values',
        minimum: 'form-error-message-minimum-numeric-value',
        maximum: 'form-error-message-maximum-numeric-value',
      },
    }),
  },
  { additionalProperties: false, $id: 'WinnersFormSchema' }
);
export const WinnersWithEmailFormSchema = Type.Object(
  {
    nbrWinners: Type.Integer({
      minimum: 1,
      maximum: 100,
      errorMessage: {
        type: 'form-error-message-numeric-values',
        minimum: 'form-error-message-minimum-numeric-value',
        maximum: 'form-error-message-maximum-numeric-value',
      },
    }),
  },
  { additionalProperties: false, $id: 'WinnersFormSchema' }
);

export const WinnersFormTransformer: ModuleTransformer<WinnersFormType> = {
  fromPayload: ({ lottery }) => {
    return {
      nbrWinners: `${lottery.nbrWinners}`,
    };
  },
  toPayload: (formData) => {
    return {
      lottery: {
        nbrWinners: parseInt(formData.nbrWinners, 10),
      },
    };
  },
};

const defaultValues: WinnersFormType = { nbrWinners: '' };

export type WinnersFormProps = FormModuleProps<WinnersFormType>;

export const WinnersForm: React.FC<WinnersFormProps> = memo(
  ({ values, onChange, onValidation }) => {
    const { template } = useContext(LotteryFormContext);

    const hasEmailCommunication =
      template?.winnerCommunication.some((lotteryModule) => lotteryModule === 'email') ?? false;
    const maximumNbrWinners = hasEmailCommunication
      ? WinnersWithEmailFormSchema.properties.nbrWinners.maximum
      : WinnersFormSchema.properties.nbrWinners.maximum;

    const { control } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema: hasEmailCommunication ? WinnersWithEmailFormSchema : WinnersFormSchema,
      onChange,
      onValidation,
    });

    return (
      <>
        <InlineMessage
          className={styles['inline-message']}
          variant="informative"
          title={`${t('form-explanation-message-nbr-of-winners-title')}`}
          body={`${t('form-explanation-message-nbr-of-winners-body')}`}
        />
        <form>
          <div className={styles['form-elements']}>
            <ControlledInputField
              control={control}
              labelKey="general-number-winners-draw"
              name="nbrWinners"
              type="number"
              showCharacterLimit={false}
              upperLimit={maximumNbrWinners}
            />
          </div>
        </form>
      </>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: WinnersFormTransformer,
  form: WinnersForm,
} as const;
